import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { WizardProvider } from './WizardContext';
import { useWizard } from './useWizard';
import type { WizardStepInfo } from './WizardStepIndicator';
import WizardShell from './core/WizardShell';
import {
  BasicInfoStep,
  OperatingHoursStep,
  CapabilitiesStep,
  OnboardingStep,
  ContactStep,
  ReviewStep,
} from './steps';
import DuplicateWarningModal from './DuplicateWarningModal';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import { WIZARD_STEPS, STEP_NAMES, TOTAL_STEPS } from '../../types/wizard';
import type { WizardState } from '../../types/wizard';
import './ClinicCreationWizard.css';

// What the user has entered, without the wizard's own navigation and
// duplicate-check bookkeeping.
const enteredData = (state: WizardState) =>
  JSON.stringify({ ...state, currentStep: 0, acknowledgedDuplicateWarning: false, duplicateCheckResult: null });

interface ClinicCreationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (clinicId: number) => void;
}

const WizardContent: React.FC<Readonly<{
  onClose: () => void;
  onComplete: (clinicId: number) => void;
}>> = ({ onClose, onComplete }) => {
  const {
    state,
    stepErrors,
    isSubmitting,
    isCheckingDuplicates,
    nextStep,
    prevStep,
    goToStep,
    submit,
    setAcknowledgedDuplicateWarning,
  } = useWizard();
  const [visitedSteps, setVisitedSteps] = useState<boolean[]>(
    Array(TOTAL_STEPS).fill(false).map((_, i) => i === 0)
  );
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  // Track whether we're waiting for a duplicate check result to show the modal
  const [pendingDuplicateCheck, setPendingDuplicateCheck] = useState(false);
  // Set when the clinic was created but one or more post-create saves failed —
  // the wizard stays open to tell the user exactly what didn't save (SLV2-3153).
  const [partialResult, setPartialResult] = useState<{ clinicId: number; warnings: string[] } | null>(null);

  // Update visited steps when current step changes
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- tracks visited steps on navigation
    setVisitedSteps(prev => {
      if (!prev[state.currentStep]) {
        const updated = [...prev];
        updated[state.currentStep] = true;
        return updated;
      }
      return prev;
    });
  }, [state.currentStep]);

  // Show duplicate modal when duplicateCheckResult updates with matches
  // This handles the React state update timing issue where the result
  // isn't available in the handleNext closure when nextStep() returns.
  useEffect(() => {
    if (
      pendingDuplicateCheck &&
      state.duplicateCheckResult?.hasPotentialDuplicates &&
      !state.acknowledgedDuplicateWarning
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- async state machine reacting to duplicate check
      setPendingDuplicateCheck(false);
      setShowDuplicateModal(true);
    }
  }, [pendingDuplicateCheck, state.duplicateCheckResult, state.acknowledgedDuplicateWarning]);

  const [initialData] = useState(() => enteredData(state));
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // Cancel, X and Escape: never mid-save, and ask before throwing away entries.
  // A click outside the wizard does not close it at all (SLV2-4049).
  const handleClose = () => {
    if (isSubmitting || isCheckingDuplicates) return;
    if (enteredData(state) !== initialData) setConfirmDiscard(true);
    else onClose();
  };

  useEffect(() => {
    // The duplicate and discard modals handle Escape themselves; the
    // partial-result panel only leaves through the clinic profile.
    if (showDuplicateModal || confirmDiscard || partialResult) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  });

  const handlePrevious = () => {
    prevStep();
  };

  const handleNext = async () => {
    const success = await nextStep();

    // Check if we stopped because of duplicates found
    if (!success && state.currentStep === WIZARD_STEPS.BASIC_INFO) {
      const basicInfoErrors = stepErrors[WIZARD_STEPS.BASIC_INFO] || {};
      if (
        Object.keys(basicInfoErrors).length === 0 &&
        !state.acknowledgedDuplicateWarning
      ) {
        // Signal that we're waiting for the duplicate check result.
        // The useEffect above will show the modal once state updates.
        setPendingDuplicateCheck(true);
        return;
      }
    }

    if (success && !visitedSteps[state.currentStep + 1]) {
      setVisitedSteps(prev => {
        const updated = [...prev];
        updated[state.currentStep + 1] = true;
        return updated;
      });
    }
  };

  const handleDuplicateEditExisting = (clinicId: number) => {
    setShowDuplicateModal(false);
    onClose();
    window.location.href = `/clinics/${clinicId}`;
  };

  const handleDuplicateContinue = async () => {
    setShowDuplicateModal(false);
    setAcknowledgedDuplicateWarning(true);

    const success = await nextStep();
    if (success && !visitedSteps[state.currentStep + 1]) {
      setVisitedSteps(prev => {
        const updated = [...prev];
        updated[state.currentStep + 1] = true;
        return updated;
      });
    }
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false);
  };

  const handleFinish = async () => {
    const { clinicId, warnings } = await submit();
    if (clinicId === null) return; // creation failed — submitError is shown on the Review step

    if (warnings.length > 0) {
      // Clinic exists but some sections didn't save — surface it instead of
      // reporting silent success.
      setPartialResult({ clinicId, warnings });
      return;
    }

    onComplete(clinicId);
  };

  const handleStepClick = (stepIndex: number) => {
    if (visitedSteps[stepIndex]) {
      goToStep(stepIndex);
    }
  };

  const isStepSkipped = (stepIndex: number): boolean => {
    if (stepIndex === WIZARD_STEPS.CAPABILITIES && !state.includeCapabilities) {
      return true;
    }
    if (stepIndex === WIZARD_STEPS.ONBOARDING && !state.includeOnboarding) {
      return true;
    }
    if (stepIndex === WIZARD_STEPS.CONTACT && !state.includeContact) {
      return true;
    }
    return false;
  };

  const steps: WizardStepInfo[] = STEP_NAMES.map((name, index) => ({
    name,
    completed: index < state.currentStep && !isStepSkipped(index),
    hasErrors: Object.keys(stepErrors[index] || {}).length > 0,
    skipped: isStepSkipped(index),
  }));

  const renderStepContent = () => {
    switch (state.currentStep) {
      case WIZARD_STEPS.BASIC_INFO:
        return <BasicInfoStep />;
      case WIZARD_STEPS.HOURS:
        return <OperatingHoursStep />;
      case WIZARD_STEPS.CAPABILITIES:
        return <CapabilitiesStep />;
      case WIZARD_STEPS.ONBOARDING:
        return <OnboardingStep />;
      case WIZARD_STEPS.CONTACT:
        return <ContactStep />;
      case WIZARD_STEPS.REVIEW:
        return <ReviewStep />;
      default:
        return null;
    }
  };

  const isLastStep = state.currentStep === WIZARD_STEPS.REVIEW;
  const canGoPrevious = state.currentStep > 0;
  const canGoNext = state.currentStep < WIZARD_STEPS.REVIEW;

  const headerContent = (
    <div className="wizard-header">
      <div className="wizard-header-left">
        <h1 className="wizard-header-title">Create New Clinic</h1>
        <p className="wizard-header-subtitle">
          Complete the steps below to add a new clinic to the system.
        </p>
      </div>
      <button
        type="button"
        className="wizard-header-close"
        onClick={handleClose}
        aria-label="Close wizard"
        disabled={isSubmitting || isCheckingDuplicates}
      >
        <X size={24} />
      </button>
    </div>
  );

  // Partial success: the clinic was created, but some sections failed to save.
  // Replace the wizard with an explicit outcome panel — re-submitting would
  // create a duplicate clinic, so the only way forward is the profile.
  if (partialResult) {
    return (
      <div className="wizard-partial-panel" role="alertdialog" aria-label="Clinic created with warnings">
        <h2>Clinic created — but some details didn't save</h2>
        <div className="alert alert-warning">
          <strong>Not saved:</strong>&nbsp;{partialResult.warnings.join(', ')}
        </div>
        <p>
          The clinic itself was created successfully. The sections listed above failed to save —
          open the clinic profile to enter them there, or try again later.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onComplete(partialResult.clinicId)}
        >
          Open Clinic Profile
        </button>
      </div>
    );
  }

  return (
    <>
      <WizardShell
        headerContent={headerContent}
        steps={steps}
        currentStep={state.currentStep}
        visitedSteps={visitedSteps}
        onStepClick={handleStepClick}
        totalSteps={TOTAL_STEPS}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
        isLastStep={isLastStep}
        isSubmitting={isSubmitting || isCheckingDuplicates}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onFinish={handleFinish}
        onCancel={handleClose}
      >
        {renderStepContent()}
      </WizardShell>

      {/* Duplicate Warning Modal */}
      <DuplicateWarningModal
        isOpen={showDuplicateModal}
        matches={state.duplicateCheckResult?.matches || []}
        onEditExisting={handleDuplicateEditExisting}
        onContinue={handleDuplicateContinue}
        onCancel={handleDuplicateCancel}
      />

      <ConfirmationModal
        isOpen={confirmDiscard}
        title="Discard this new clinic?"
        message="Everything entered in the wizard will be lost."
        confirmText="Discard"
        cancelText="Keep Editing"
        danger
        onConfirm={onClose}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>
  );
};

const ClinicCreationWizard: React.FC<Readonly<ClinicCreationWizardProps>> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  if (!isOpen) return null;

  return (
    <div className="wizard-overlay">
      <WizardProvider>
        <WizardContent onClose={onClose} onComplete={onComplete} />
      </WizardProvider>
    </div>
  );
};

export default ClinicCreationWizard;
